import axios, { AxiosResponse } from 'axios';
import { Dispatch } from 'redux';

// export const GET_PARENT_DATA = "GET_PARENT_DATA";
export const ADD_STUDENT = 'ADD_STUDENT';
export const SET_STUDENT_DATA = 'SET_STUDENT_DATA';
export const GET_STUDENTS = 'GET_STUDENTS';
export const UPDATE_STUDENT = 'UPDATE_STUDENT';
export const SET_SMS_PHONE = 'SET_SMS_PHONE';
export const SET_PARENT_DATA = 'SET_PARENT_DATA';
export const GET_PARENT_DATA = 'GET_PARENT_DATA';
export const UPDATING_PARENT_DATA = 'UPDATING_PARENT_DATA';
export const UPDATE_PARENT_ADDRESS_SUCCESS = 'UPDATE_PARENT_ADDRESS_SUCCESS';
export const UPDATE_PARENT_ADDRESS_FAILURE = 'UPDATE_PARENT_ADDRESS_FAILURE';
export const UPDATE_PARENT_REQUEST = 'UPDATE_PARENT_REQUEST';
export const UPDATE_PARENT_SUCCESS = 'UPDATE_PARENT_SUCCESS';
export const UPDATE_PARENT_FAILURE = 'UPDATE_PARENT_FAILURE';
export const UPDATE_PASSWORD_REQUEST = 'UPDATE_PASSWORD_REQUEST';
export const UPDATE_PASSWORD_SUCCESS = 'UPDATE_PASSWORD_SUCCESS';
export const UPDATE_PASSWORD_FAILURE = 'UPDATE_PASSWORD_FAILURE';
export const SEND_EMAIL_CONFIRMATION_REQUEST =
  'SEND_EMAIL_CONFIRMATION_REQUEST';
export const SEND_EMAIL_CONFIRMATION_SUCCESS =
  'SEND_EMAIL_CONFIRMATION_SUCCESS';
export const SEND_EMAIL_CONFIRMATION_FAILURE =
  'SEND_EMAIL_CONFIRMATION_FAILURE';
export const CONFIRM_EMAIL_REQUEST = 'CONFIRM_EMAIL_REQUEST';
export const CONFIRM_EMAIL_SUCCESS = 'CONFIRM_EMAIL_SUCCESS';
export const CONFIRM_EMAIL_FAILURE = 'CONFIRM_EMAIL_FAILURE';
export const ADD_STUDENT_SUCCESS = 'ADD_STUDENT_SUCCESS';
export const RESET_SUCCESS_MESSAGE = 'RESET_SUCCESS_MESSAGE';
export const VIEWED_COMPANY_MESSAGE = 'VIEWED_COMPANY_MESSAGE';
export const DISMISS_UNSUBSCRIBE_BANNER = 'DISMISS_UNSUBSCRIBE_BANNER';
export const SET_UNSUBSCRIBE_STATUS = 'SET_UNSUBSCRIBE_STATUS';

const apiUrl = '/api/v2';

// Payload is a promise: redux-promise dispatches the settled action.
interface PromiseAction {
  type: string;
  payload: Promise<AxiosResponse>;
}

type RequestData = Record<string, unknown>;

export function getParentData(): PromiseAction {
  const request = axios.get(`${apiUrl}/parents/get_parent_data`);

  return {
    type: GET_PARENT_DATA,
    payload: request,
  };
}

export function addStudent(data: RequestData): PromiseAction {
  const request = axios.post(`${apiUrl}/students`, data);

  return {
    type: ADD_STUDENT,
    payload: request,
  };
}

// Concurrent callers share one request. App fetches students as soon as
// setCurrentUser resolves, and BnlParentDashboard fetches them again on mount
// to chain getGalleries off the result, so landing on the dashboard issued two
// identical requests within milliseconds. Both call sites are load-bearing
// (other routes rely on App's; the dashboard needs the students to ask for
// their galleries), so the duplicate is collapsed here instead.
//
// Only in-flight requests are shared, and only for callers that just want the
// current list. A caller refreshing after a mutation must pass
// { refresh: true }: it would otherwise be handed a request that was already
// on the wire before the student existed, and Redux would receive a list
// without them.
const inFlightStudentRequests = new Map<string, Promise<AxiosResponse>>();

export function getStudents(
  parentId: number | string,
  { refresh = false }: { refresh?: boolean } = {},
): PromiseAction {
  const key = String(parentId);
  let request = refresh ? undefined : inFlightStudentRequests.get(key);

  if (!request) {
    request = axios
      .get(`/api/private/parents/${parentId}/students`)
      // A refresh replaces the shared entry, so only clear it if this request
      // is still the one parked there.
      .finally(() => {
        if (inFlightStudentRequests.get(key) === request) {
          inFlightStudentRequests.delete(key);
        }
      });

    inFlightStudentRequests.set(key, request);
  }

  return {
    type: GET_STUDENTS,
    payload: request,
  };
}

export function setStudentData(studentData: unknown) {
  console.log('SET STUDENT DATA', studentData);

  return {
    type: SET_STUDENT_DATA,
    payload: studentData,
  };
}

// email and publicToken are unused by the body but part of the existing
// signature: CustomerAddressEdit passes all three. Kept so converting this
// file to TypeScript does not quietly narrow the contract.
export function updateParentAddress(
  addressData: unknown,
  email?: string,
  publicToken?: string,
) {
  return {
    type: UPDATE_PARENT_ADDRESS_SUCCESS,
    payload: addressData,
  };
}

export const updateParent =
  (requestData: RequestData) =>
  async (dispatch: Dispatch): Promise<AxiosResponse> => {
    dispatch({ type: UPDATE_PARENT_REQUEST });

    try {
      const response = await axios.patch('/api/v2/parents/update', {
        ...requestData,
      });
      const responseData = response.data;

      dispatch({
        type: UPDATE_PARENT_SUCCESS,
        payload: responseData,
      });

      return response;
    } catch (error) {
      dispatch({ type: UPDATE_PARENT_FAILURE });

      return Promise.reject(error);
    }
  };

export const updatePassword =
  (requestData: RequestData) =>
  async (dispatch: Dispatch): Promise<AxiosResponse> => {
    dispatch({ type: UPDATE_PASSWORD_REQUEST });

    try {
      const response = await axios.patch('/api/v2/parents/update-password', {
        ...requestData,
      });

      dispatch({ type: UPDATE_PASSWORD_SUCCESS });
      return response;
    } catch (error) {
      dispatch({ type: UPDATE_PASSWORD_FAILURE });
      return Promise.reject(error);
    }
  };

export const sendEmailConfirmation =
  (requestData: RequestData) =>
  async (dispatch: Dispatch): Promise<AxiosResponse> => {
    dispatch({ type: SEND_EMAIL_CONFIRMATION_REQUEST });

    try {
      const response = await axios.post(
        '/api/v2/parents/send-confirmation-email',
        {
          ...requestData,
        },
      );

      dispatch({
        type: SEND_EMAIL_CONFIRMATION_SUCCESS,
        payload: response.data,
      });
      return response;
    } catch (error) {
      dispatch({ type: SEND_EMAIL_CONFIRMATION_FAILURE });
      return Promise.reject(error);
    }
  };

export const confirmEmail =
  (requestData: { token: string } & RequestData) =>
  async (dispatch: Dispatch): Promise<AxiosResponse> => {
    dispatch({ type: CONFIRM_EMAIL_REQUEST });
    try {
      const response = await axios.post(
        `/api/v2/parents/${requestData.token}/confirm-email`,
        {
          ...requestData,
        },
      );
      const responseData = response.data;

      dispatch({
        type: CONFIRM_EMAIL_SUCCESS,
        payload: responseData,
      });

      return response;
    } catch (error) {
      dispatch({ type: CONFIRM_EMAIL_FAILURE });

      return Promise.reject(error);
    }
  };

export function addStudentSuccess(student: unknown) {
  return {
    type: ADD_STUDENT_SUCCESS,
    payload: student,
  };
}

export function setSmsPhone(smsPhone: string) {
  return {
    type: SET_SMS_PHONE,
    payload: smsPhone,
  };
}

export function viewedCompanyMessage() {
  return {
    type: VIEWED_COMPANY_MESSAGE,
    payload: true,
  };
}
